import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Calendar,
  Check,
  HelpCircle,
  Home,
  Loader2,
  Plus,
  RotateCcw,
  Save,
  Upload,
  Video,
  X,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { courseService } from '../services/courseService'
import mediaService, { uploadCourseImage, uploadCourseVideo } from '../services/mediaService'
import { yogaSchoolService, YogaSchool } from '../services/yogaSchoolService'
import adminRoomService, { Room } from '../services/adminRoomService'
import { MEDIA_SECTIONS, validateImageFiles, validateVideoFiles } from '../config/mediaValidation'
import { PATTERNS, validateCourse } from '../validation/course'
import CourseDateRangesEditor from '../components/CourseDateRangesEditor'
import {
  createEmptyDateRangeRow,
  formatDateRangesSummary,
  normalizeDateRangeRows,
  sortDateRangeRows,
  type CourseDateRangeRow,
} from '../utils/courseSchedule'
import RoomTypesManageModal from '../components/RoomTypesManageModal'

/**
 * Admin-driven course creation.
 *
 * This page is an admin-side mirror of the yoga school dashboard's
 * `CreateCourse.tsx` wizard. The form fields, validation, and API payload are
 * 100% identical — only the context differs:
 *
 *   - There is no logged-in "school" so the admin explicitly picks one from a
 *     dropdown at the top.
 *   - There is no draft autosave (sessionStorage), because there's no 1:1
 *     school-user binding.
 *   - Room types can only be *selected* from what the school already has; if
 *     the school has zero rooms, the admin is told to add them from the
 *     school's own dashboard first (room management stays in school scope).
 *   - No emails/notifications are fired by the backend for admin-created
 *     courses (that's handled by `POST /v3/courses`).
 */

const ALLOWED_COURSE_CURRENCIES = ['USD', 'EUR', 'INR', 'IDR'] as const

function normalizeCourseCurrency(code: string | undefined): string {
  const c = String(code || 'USD').toUpperCase()
  return (ALLOWED_COURSE_CURRENCIES as readonly string[]).includes(c) ? c : 'USD'
}

const YOGA_STYLES = [
  'Hatha Yoga',
  'Raja Yoga',
  'Karma Yoga',
  'Bhakti Yoga',
  'Jnana Yoga',
  'Kundalini Yoga',
  'Tantra Yoga',
  'Ashtanga Yoga',
  'Dhyana Yoga',
  'Mantra Yoga',
  'Laya Yoga',
  'Nada Yoga',
  'Kriya Yoga',
  'Sahaja Yoga',
  'Integral Yoga',
  'Viniyoga',
  'Sivananda Yoga',
  'Iyengar Yoga',
  'Bihar Yoga',
  'Satyananda Yoga',
  'Kaivalya Yoga',
  'Patanjali Yoga',
  'Agama Yoga',
  'Shakta Yoga',
  'Shaiva Yoga',
  'Vaishnava Yoga',
  'Brahma Yoga',
  'Advaita Yoga',
  'Vinyasa Yoga',
]

type TimeSlot = { label?: string; startTime: string; endTime: string }
type CurriculumSection = { title: string; items: string[] }
type FaqItem = { question: string; answer: string }
type AccommodationRoom = {
  roomId?: string
  type?: string
  capacity?: number
  price: number
  bookingAmount?: number
  amenities?: string[]
}

interface CourseForm {
  schoolId: string
  name: string
  type: 'TRAINING' | 'RETREAT'
  description: string
  shortDescription?: string
  price?: number
  currency: string
  duration: number
  maxStudents?: number
  minStudents?: number
  difficulty?: 'ALL_LEVELS' | 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED'
  yogaStyles: string[]
  schedule: {
    startDate?: string
    endDate?: string
    timeSlots: TimeSlot[]
  }
  startDay: number
  inclusions: string[]
  requirements: string[]
  whoIsThisFor: string[]
  courseHighlights: string[]
  whatYouWillGain: string[]
  curriculum: CurriculumSection[]
  faq: FaqItem[]
  media: { images: string[]; videos: string[] }
  accommodation: {
    available: boolean
    rooms: AccommodationRoom[]
    mealPlan: { included: boolean; type: 'VEGETARIAN' | 'VEGAN' | 'OMNIVORE' | 'CUSTOM'; description?: string }
  }
  images: string[]
  yogaAllianceCertified: boolean
  isActive: boolean
  isFeatured: boolean
}

function buildEmptyForm(): CourseForm {
  return {
    schoolId: '',
    name: '',
    type: 'TRAINING',
    description: '',
    shortDescription: '',
    price: undefined,
    currency: 'USD',
    duration: 1,
    maxStudents: undefined,
    minStudents: undefined,
    difficulty: undefined,
    yogaStyles: [],
    schedule: { startDate: '', endDate: '', timeSlots: [] },
    startDay: 1,
    inclusions: [],
    requirements: [],
    whoIsThisFor: [],
    courseHighlights: [],
    whatYouWillGain: [],
    curriculum: [],
    faq: [],
    media: { images: [], videos: [] },
    accommodation: {
      available: false,
      rooms: [],
      mealPlan: { included: false, type: 'VEGETARIAN' },
    },
    images: [],
    yogaAllianceCertified: false,
    isActive: false,
    isFeatured: false,
  }
}

function parseTimeToMinutes(timeStr: string): number | null {
  if (!timeStr || typeof timeStr !== 'string') return null
  const parts = timeStr.trim().split(':')
  const h = parseInt(parts[0], 10)
  const m = parseInt(parts[1] || '0', 10)
  if (isNaN(h) || isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) return null
  return h * 60 + m
}

function minutesToTime(minutes: number): string {
  const capped = Math.max(0, Math.min(minutes, 23 * 60 + 59))
  const h = Math.floor(capped / 60)
  const m = capped % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

const AddCourse: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // School picker (search-as-you-type combobox)
  const [schoolSearch, setSchoolSearch] = useState('')
  const [schoolResults, setSchoolResults] = useState<YogaSchool[]>([])
  const [searchingSchools, setSearchingSchools] = useState(false)
  const [showSchoolResults, setShowSchoolResults] = useState(false)
  const [selectedSchool, setSelectedSchool] = useState<YogaSchool | null>(null)
  const schoolBoxRef = useRef<HTMLDivElement>(null)

  // Wizard state
  const [currentStep, setCurrentStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState<CourseForm>(() => {
    // Allow callers to deep-link with ?schoolId=... so the wizard opens with
    // the right school already selected (e.g. from the Courses page filter
    // or from a school detail page).
    const preselected = searchParams.get('schoolId') || ''
    return { ...buildEmptyForm(), schoolId: preselected }
  })
  const [dateRangeRows, setDateRangeRows] = useState<CourseDateRangeRow[]>(() => [
    createEmptyDateRangeRow(),
  ])

  const handleDateRangeRowsChange = (rows: CourseDateRangeRow[]) => {
    setDateRangeRows(sortDateRangeRows(rows))
  }
  const [textareaValues, setTextareaValues] = useState<Record<string, string>>({})

  // Accommodation rooms (scoped to selected school)
  const [schoolRooms, setSchoolRooms] = useState<Room[]>([])
  const [loadingRooms, setLoadingRooms] = useState(false)
  const [roomTypesModalOpen, setRoomTypesModalOpen] = useState(false)

  // Media
  const [uploadingMedia, setUploadingMedia] = useState(false)
  const [mediaUrls, setMediaUrls] = useState<{ images: string[]; videos: string[] }>({
    images: [],
    videos: [],
  })
  const imageInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)

  const steps = [
    { id: 1, title: 'Basic Info', icon: BookOpen, description: 'Course details and pricing' },
    { id: 2, title: 'Schedule', icon: Calendar, description: 'Dates and timing' },
    { id: 3, title: 'Accommodation', icon: Home, description: 'Room and meal options' },
    { id: 4, title: 'Review', icon: Check, description: 'Review and create' },
  ]

  // Search-as-you-type for the school picker. We only hit the API once the
  // user has typed something, so by default no list is loaded.
  useEffect(() => {
    const term = schoolSearch.trim()
    if (term.length < 2) {
      setSchoolResults([])
      setSearchingSchools(false)
      return
    }
    let cancelled = false
    setSearchingSchools(true)
    const handle = setTimeout(async () => {
      try {
        const response = await yogaSchoolService.getYogaSchools({
          page: 1,
          limit: 20,
          search: term,
        })
        if (cancelled) return
        if (response?.success && response.data?.schools) {
          setSchoolResults(response.data.schools)
        } else {
          setSchoolResults([])
        }
      } catch (err) {
        if (!cancelled) setSchoolResults([])
      } finally {
        if (!cancelled) setSearchingSchools(false)
      }
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [schoolSearch])

  // If the page was opened with `?schoolId=...` we need to resolve the school
  // once so the user sees its name in the picker. This is a one-shot lookup
  // that doesn't list anything.
  useEffect(() => {
    const preselectId = formData.schoolId
    if (!preselectId || selectedSchool?._id === preselectId) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await yogaSchoolService.getYogaSchoolById(preselectId)
        if (!cancelled && res?.success && res.data) {
          setSelectedSchool(res.data)
        }
      } catch {
        // ignore — user can search for it manually
      }
    })()
    return () => {
      cancelled = true
    }
  // We intentionally only react to the initial schoolId; later changes go
  // through the picker which sets selectedSchool itself.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Close the dropdown when clicking outside.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (schoolBoxRef.current && !schoolBoxRef.current.contains(e.target as Node)) {
        setShowSchoolResults(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  // When the admin picks a school, fetch its rooms so the accommodation step
  // can show a dropdown of real room types. Reusable so the inline
  // RoomTypesManageModal can refresh the list after a create/delete/toggle.
  const fetchSchoolRooms = React.useCallback(async (schoolId: string | undefined | null) => {
    if (!schoolId) {
      setSchoolRooms([])
      return
    }
    try {
      setLoadingRooms(true)
      const rooms = await adminRoomService.getSchoolRooms(schoolId, true)
      setSchoolRooms(rooms)
    } catch (err) {
      console.error('Failed to load school rooms', err)
      setSchoolRooms([])
    } finally {
      setLoadingRooms(false)
    }
  }, [])

  useEffect(() => {
    fetchSchoolRooms(formData.schoolId)
  }, [formData.schoolId, fetchSchoolRooms])

  // Resolve media fileKeys to display URLs.
  useEffect(() => {
    const convert = () => {
      const validImages = (formData.media?.images || []).filter(
        (k) => k && !k.startsWith('blob:') && !k.startsWith('http://localhost'),
      )
      const validVideos = (formData.media?.videos || []).filter(
        (k) => k && !k.startsWith('blob:') && !k.startsWith('http://localhost'),
      )
      const imageUrls = validImages.map((key) =>
        key.startsWith('http://') || key.startsWith('https://') ? key : mediaService.getMediaUrlFromKey(key),
      )
      const videoUrls = validVideos.map((key) =>
        key.startsWith('http://') || key.startsWith('https://') ? key : mediaService.getMediaUrlFromKey(key),
      )
      setMediaUrls({ images: imageUrls, videos: videoUrls })
    }
    convert()
  }, [formData.media?.images, formData.media?.videos])

  // ──────────────────────────────────────────────────────────────────────
  // Form mutation helpers

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleNestedChange = (parent: string, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [parent]: {
        ...(prev[parent as keyof CourseForm] as any),
        [field]: value,
      },
    }))
  }

  const handleArrayAdd = (field: string, item: any) => {
    setFormData((prev) => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.')
        const parentValue = prev[parent as keyof CourseForm] as any
        const currentArray = parentValue?.[child] || []
        return {
          ...prev,
          [parent]: {
            ...parentValue,
            [child]: [...(Array.isArray(currentArray) ? currentArray : []), item],
          },
        }
      }
      const currentArray = (prev as any)[field]
      return {
        ...prev,
        [field]: [...(Array.isArray(currentArray) ? currentArray : []), item],
      }
    })
  }

  const handleArrayRemove = (field: string, index: number) => {
    setFormData((prev) => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.')
        const parentValue = prev[parent as keyof CourseForm] as any
        const currentArray = parentValue?.[child] || []
        return {
          ...prev,
          [parent]: {
            ...parentValue,
            [child]: (Array.isArray(currentArray) ? currentArray : []).filter((_: any, i: number) => i !== index),
          },
        }
      }
      const currentArray = (prev as any)[field]
      return {
        ...prev,
        [field]: (Array.isArray(currentArray) ? currentArray : []).filter((_: any, i: number) => i !== index),
      }
    })
  }

  const handleTimeSlotAdd = () => {
    const slots = formData.schedule?.timeSlots || []
    let newSlot: TimeSlot
    if (slots.length > 0) {
      const lastSlot = slots[slots.length - 1]
      const startMins = parseTimeToMinutes(lastSlot.startTime || '')
      const endMins = parseTimeToMinutes(lastSlot.endTime || '')
      if (startMins != null && endMins != null && endMins > startMins) {
        const durationMins = endMins - startMins
        newSlot = {
          startTime: minutesToTime(endMins),
          endTime: minutesToTime(endMins + durationMins),
          label: '',
        }
      } else {
        newSlot = { startTime: '09:00', endTime: '09:30', label: '' }
      }
    } else {
      newSlot = { startTime: '09:00', endTime: '09:30', label: '' }
    }
    handleArrayAdd('schedule.timeSlots', newSlot)
  }

  const handleRoomAdd = () => {
    handleArrayAdd('accommodation.rooms', {
      roomId: '',
      type: 'CUSTOM' as const,
      capacity: 0,
      price: 0,
      bookingAmount: undefined,
      amenities: [],
    } satisfies AccommodationRoom)
  }

  const handleTextareaArrayChange = (field: string, value: string) => {
    setTextareaValues((prev) => ({ ...prev, [field]: value }))
    const items = value.split('\n').map((item) => item.trim()).filter((item) => item.length > 0)
    handleInputChange(field, items)
  }

  const getTextareaValue = (field: string, arr: string[] | undefined): string => {
    if (textareaValues[field] !== undefined) return textareaValues[field]
    return arr && arr.length > 0 ? arr.join('\n') : ''
  }

  // ──────────────────────────────────────────────────────────────────────
  // Media upload

  const handleMultipleMediaUpload = async (files: File[], type: 'image' | 'video') => {
    if (!files.length) return
    if (!formData.schoolId) {
      toast.error('Select a school first')
      return
    }

    const uploadedImages = (formData.media?.images || []).filter(
      (k) => k && !k.startsWith('blob:') && !k.startsWith('http://localhost'),
    ).length
    const uploadedVideos = (formData.media?.videos || []).filter(
      (k) => k && !k.startsWith('blob:') && !k.startsWith('http://localhost'),
    ).length

    if (type === 'image') {
      const validation = validateImageFiles(files, 'courseMedia', uploadedImages)
      if (!validation.valid) {
        toast.error(validation.error || 'Validation failed')
        return
      }
    } else {
      const validation = validateVideoFiles(files, 'courseMedia', uploadedVideos)
      if (!validation.valid) {
        toast.error(validation.error || 'Validation failed')
        return
      }
    }

    const maxImages = MEDIA_SECTIONS.courseMedia.maxImages ?? 5
    const maxVideos = MEDIA_SECTIONS.courseMedia.maxVideos ?? 2
    const toAdd =
      type === 'image'
        ? files.slice(0, Math.max(0, maxImages - uploadedImages))
        : files.slice(0, Math.max(0, maxVideos - uploadedVideos))
    if (toAdd.length === 0) return

    setUploadingMedia(true)
    const fileKeys: string[] = []
    try {
      for (const file of toAdd) {
        const result =
          type === 'image'
            ? await uploadCourseImage(file, formData.schoolId)
            : await uploadCourseVideo(file, formData.schoolId)
        if (result.success && result.data?.fileKey) fileKeys.push(result.data.fileKey)
        else toast.error(`Failed to upload ${file.name}`)
      }
      if (fileKeys.length > 0) {
        const currentMedia = formData.media || { images: [], videos: [] }
        const cleanImages = (currentMedia.images || []).filter(
          (k) => k && !k.startsWith('blob:') && !k.startsWith('http://localhost'),
        )
        const cleanVideos = (currentMedia.videos || []).filter(
          (k) => k && !k.startsWith('blob:') && !k.startsWith('http://localhost'),
        )
        handleInputChange('media', {
          images: type === 'image' ? [...cleanImages, ...fileKeys] : cleanImages,
          videos: type === 'video' ? [...cleanVideos, ...fileKeys] : cleanVideos,
        })
        toast.success(`${fileKeys.length} ${type === 'image' ? 'image(s)' : 'video(s)'} uploaded`)
      }
    } catch (err: any) {
      toast.error(err?.message || `Failed to upload ${type}s`)
    } finally {
      setUploadingMedia(false)
    }
  }

  const handleRemoveCourseImage = (displayIndex: number) => {
    const validImageKeys = (formData.media?.images || []).filter(
      (k) => k && !k.startsWith('blob:') && !k.startsWith('http://localhost'),
    )
    const newImages = validImageKeys.filter((_, i) => i !== displayIndex)
    handleInputChange('media', { ...formData.media, images: newImages, videos: formData.media?.videos || [] })
  }

  const handleRemoveCourseVideo = (displayIndex: number) => {
    const validVideoKeys = (formData.media?.videos || []).filter(
      (k) => k && !k.startsWith('blob:') && !k.startsWith('http://localhost'),
    )
    const newVideos = validVideoKeys.filter((_, i) => i !== displayIndex)
    handleInputChange('media', { ...formData.media, images: formData.media?.images || [], videos: newVideos })
  }

  // ──────────────────────────────────────────────────────────────────────
  // Wizard navigation + validation

  const nextStep = () => {
    if (currentStep === 1) {
      if (!formData.schoolId) {
        toast.error('Select a school first')
        return
      }
    }

    if (currentStep === 2) {
      const slots = formData.schedule?.timeSlots || []
      if (slots.length === 0) {
        toast.error('At least one time slot is required. Use "Add Time Slot" under Daily Schedule.', {
          id: 'schedule-validation',
        })
        return
      }
      const slotWithoutLabel = slots.find((s) => !(s.label || '').trim())
      if (slotWithoutLabel) {
        toast.error('Label is required for each time slot.', { id: 'schedule-validation' })
        return
      }
      for (let i = 0; i < slots.length; i++) {
        const ts = slots[i]
        if (ts?.startTime && !PATTERNS.time24.test(ts.startTime)) {
          toast.error(`Time slot ${i + 1}: start time must be in HH:MM format (24h).`, {
            id: 'schedule-validation',
          })
          return
        }
        if (ts?.endTime && !PATTERNS.time24.test(ts.endTime)) {
          toast.error(`Time slot ${i + 1}: end time must be in HH:MM format (24h).`, {
            id: 'schedule-validation',
          })
          return
        }
      }
    }

    if (currentStep === 3 && formData.accommodation?.available && (!schoolRooms || schoolRooms.length === 0)) {
      toast.error('This school has no room types yet. Use "Add room types" above to create one, or turn off accommodation.')
      return
    }

    if (currentStep === 3 && formData.accommodation?.available && formData.accommodation.rooms?.length) {
      const rooms = formData.accommodation.rooms
      for (let i = 0; i < rooms.length; i++) {
        const r = rooms[i]
        const price = Number(r.price) || 0
        const bookingAmount = r.bookingAmount != null ? Number(r.bookingAmount) : undefined
        if (price <= 0) continue
        if (bookingAmount == null || bookingAmount <= 0) {
          toast.error('Booking amount is required for each room.', { id: 'booking-validation' })
          return
        }
        if (bookingAmount <= price * 0.1) {
          const minAmount = (price * 0.1 + 1).toFixed(2)
          toast.error(
            `Booking amount must be more than 10% of the room price (min ${minAmount} ${formData.currency}).`,
            { id: 'booking-validation' },
          )
          return
        }
        if (bookingAmount > price) {
          toast.error(`Booking amount cannot exceed the room price (${price.toFixed(2)} ${formData.currency}).`, {
            id: 'booking-validation',
          })
          return
        }
      }
    }

    if (currentStep < steps.length) setCurrentStep(currentStep + 1)
  }

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1)
  }

  const handleReset = () => {
    if (!window.confirm('Reset the form? All entered data will be lost.')) return
    setFormData(buildEmptyForm())
    setDateRangeRows([createEmptyDateRangeRow()])
    setTextareaValues({})
    setCurrentStep(1)
    setMediaUrls({ images: [], videos: [] })
    toast.success('Form reset')
  }

  // ──────────────────────────────────────────────────────────────────────
  // Submit

  const handleSubmit = async () => {
    if (!formData.schoolId) {
      toast.error('Select a school first')
      setCurrentStep(1)
      return
    }

    const errors = validateCourse({
      name: formData.name,
      description: formData.description,
      shortDescription: formData.shortDescription,
      duration: formData.duration,
      startDay: formData.startDay,
      dateRangeRows,
      schedule: formData.schedule,
      inclusions: formData.inclusions,
      requirements: formData.requirements,
      whoIsThisFor: formData.whoIsThisFor,
      courseHighlights: formData.courseHighlights,
      whatYouWillGain: formData.whatYouWillGain,
      curriculum: formData.curriculum,
      media: formData.media,
      accommodation: formData.accommodation,
    })
    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0]
      toast.error(firstError)
      return
    }

    if (
      formData.accommodation?.available &&
      (!formData.accommodation?.rooms?.length || formData.accommodation.rooms.every((r) => !r.roomId))
    ) {
      toast.error('Add at least one room type in the Accommodation step.')
      setCurrentStep(3)
      return
    }

    if (formData.accommodation?.available && formData.accommodation.rooms?.length) {
      const rooms = formData.accommodation.rooms
      for (let i = 0; i < rooms.length; i++) {
        const r = rooms[i]
        const price = Number(r.price) || 0
        const bookingAmount = r.bookingAmount != null ? Number(r.bookingAmount) : undefined
        if (price <= 0) continue
        if (bookingAmount == null || bookingAmount <= 0) {
          toast.error('Booking amount is required for each room.', { id: 'booking-validation' })
          setCurrentStep(3)
          return
        }
        if (bookingAmount <= price * 0.1) {
          const minAmount = (price * 0.1 + 1).toFixed(2)
          toast.error(
            `Booking amount must be more than 10% of the room price (min ${minAmount} ${formData.currency}).`,
            { id: 'booking-validation' },
          )
          setCurrentStep(3)
          return
        }
        if (bookingAmount > price) {
          toast.error(`Booking amount cannot exceed the room price (${price.toFixed(2)} ${formData.currency}).`, {
            id: 'booking-validation',
          })
          setCurrentStep(3)
          return
        }
      }
    }

    const scheduleData: any = {
      timeSlots: (formData.schedule?.timeSlots || []).map((ts) => ({
        label: ts?.label || '',
        startTime: ts?.startTime || '',
        endTime: ts?.endTime || '',
      })),
      dateRanges: normalizeDateRangeRows(dateRangeRows),
    }
    if (formData.schedule?.startDate) scheduleData.startDate = formData.schedule.startDate
    if (formData.schedule?.endDate) scheduleData.endDate = formData.schedule.endDate

    const courseData: any = {
      schoolId: formData.schoolId,
      name: formData.name,
      type: formData.type,
      description: formData.description,
      shortDescription: formData.shortDescription,
      currency: normalizeCourseCurrency(formData.currency),
      duration: formData.duration,
      schedule: scheduleData,
      inclusions: formData.inclusions || [],
      requirements: formData.requirements || [],
      whoIsThisFor: formData.whoIsThisFor || [],
      courseHighlights: formData.courseHighlights || [],
      whatYouWillGain: formData.whatYouWillGain || [],
      curriculum: (formData.curriculum || []).filter(
        (section) => section.title.trim().length > 0 && section.items.length > 0,
      ),
      faq: (formData.faq || []).filter((item) => item.question?.trim() && item.answer?.trim()),
      media: {
        images: (formData.media?.images || [])
          .filter((url) => url.trim().length > 0)
          .filter((url) => !url.startsWith('blob:') && !url.startsWith('http://localhost')),
        videos: (formData.media?.videos || [])
          .filter((url) => url.trim().length > 0)
          .filter((url) => !url.startsWith('blob:') && !url.startsWith('http://localhost')),
      },
      accommodation: formData.accommodation
        ? {
            ...formData.accommodation,
            rooms: (formData.accommodation.rooms || [])
              .filter((r) => !!r?.roomId)
              .map((r) => ({
                roomId: r.roomId as string,
                price: Number(r.price) || 0,
                bookingAmount: Number(r.bookingAmount) || 0,
              })),
          }
        : undefined,
      images: formData.images || [],
      yogaAllianceCertified: !!formData.yogaAllianceCertified,
      isActive: !!formData.isActive,
      isFeatured: !!formData.isFeatured,
      startDay: formData.startDay,
    }

    if (formData.price !== undefined && formData.price !== null) courseData.price = formData.price
    if (formData.maxStudents !== undefined && formData.maxStudents !== null) courseData.maxStudents = formData.maxStudents
    if (formData.minStudents !== undefined && formData.minStudents !== null) courseData.minStudents = formData.minStudents
    if (formData.difficulty) courseData.difficulty = formData.difficulty
    if (formData.yogaStyles && formData.yogaStyles.length > 0) {
      courseData.yogaStyles = formData.yogaStyles.filter((style) => style.trim().length > 0)
    }

    setIsSubmitting(true)
    try {
      const response = await courseService.createCourse(courseData)
      if (response?.success) {
        toast.success('Course created successfully')
        navigate('/admin/courses')
      } else {
        toast.error(response?.message || 'Failed to create course')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create course')
    } finally {
      setIsSubmitting(false)
    }
  }

  const pickSchool = (school: YogaSchool) => {
    setSelectedSchool(school)
    handleInputChange('schoolId', school._id)
    setSchoolSearch('')
    setSchoolResults([])
    setShowSchoolResults(false)
  }

  const clearSchool = () => {
    setSelectedSchool(null)
    handleInputChange('schoolId', '')
    setSchoolSearch('')
    setSchoolResults([])
  }

  // ──────────────────────────────────────────────────────────────────────
  // Render

  return (
    <>
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 shadow-2xl rounded-2xl">
        <div className="relative flex items-center justify-between p-4">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-xl ring-2 ring-white/30">
              <BookOpen className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Create New Course</h1>
              <p className="text-white/90 text-sm">Onboard a course on behalf of a yoga school</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/admin/courses')}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 border border-white/30"
          >
            <ArrowLeft size={16} />
            Back to Courses
          </button>
        </div>
      </div>

      {/* School picker — search-as-you-type, no list is loaded by default. */}
      <div className="bg-white rounded-2xl shadow-sm border border-ivory-200 p-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Yoga School *</label>

        {selectedSchool ? (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border border-amber-200 bg-amber-50 rounded-lg">
            <div className="min-w-0">
              <div className="font-medium text-gray-900 truncate">{selectedSchool.name}</div>
              <div className="text-xs text-gray-500 truncate">
                {[selectedSchool.city, selectedSchool.country].filter(Boolean).join(', ') || selectedSchool.slug}
                {selectedSchool.approvalStatus && selectedSchool.approvalStatus !== 'APPROVED' && (
                  <span className="text-amber-700">
                    {' '}
                    · {selectedSchool.approvalStatus.toLowerCase()} (course will be created inactive)
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={clearSchool}
              className="text-sm text-amber-700 hover:text-amber-900 font-medium whitespace-nowrap"
            >
              Change
            </button>
          </div>
        ) : (
          <div className="relative" ref={schoolBoxRef}>
            <input
              type="text"
              value={schoolSearch}
              onChange={(e) => {
                setSchoolSearch(e.target.value)
                setShowSchoolResults(true)
              }}
              onFocus={() => setShowSchoolResults(true)}
              placeholder="Search yoga schools by name..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />

            {showSchoolResults && schoolSearch.trim().length >= 2 && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                {searchingSchools ? (
                  <div className="px-4 py-3 text-sm text-gray-500">Searching...</div>
                ) : schoolResults.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-500">No schools match "{schoolSearch}"</div>
                ) : (
                  schoolResults.map((s) => (
                    <button
                      key={s._id}
                      type="button"
                      onClick={() => pickSchool(s)}
                      className="w-full text-left px-4 py-3 hover:bg-amber-50 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium text-gray-900">{s.name}</div>
                      <div className="text-xs text-gray-500">
                        {[s.city, s.country].filter(Boolean).join(', ') || s.slug}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}

            <p className="text-xs text-gray-500 mt-2">
              Type at least 2 characters to search. Results come from the live schools directory.
            </p>
          </div>
        )}
      </div>

      {/* Wizard card */}
      <div className="bg-white rounded-2xl shadow-xl border border-ivory-200 overflow-hidden">
        {/* Step header */}
        <div className="relative overflow-hidden bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 px-4 py-3">
          <div className="relative flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-xl ring-2 ring-white/30">
                {React.createElement(steps[currentStep - 1].icon, { size: 20, className: 'text-white' })}
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">{steps[currentStep - 1].title}</h2>
                <p className="text-white/90 text-sm">{steps[currentStep - 1].description}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-sm font-semibold text-white">Step {currentStep}</div>
                <div className="text-white/80 text-xs">of {steps.length}</div>
              </div>
              <button
                type="button"
                onClick={handleReset}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 border border-white/30"
                title="Reset form"
              >
                <RotateCcw size={16} />
                <span>Reset</span>
              </button>
            </div>
          </div>
        </div>

        {/* Step body */}
        <div className="p-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
              className="space-y-8"
            >
              {currentStep === 1 && <Step1Basic
                formData={formData}
                textareaValues={textareaValues}
                setTextareaValues={setTextareaValues}
                onChange={handleInputChange}
                onTextarea={handleTextareaArrayChange}
                getTextareaValue={getTextareaValue}
                onArrayAdd={handleArrayAdd}
                onArrayRemove={handleArrayRemove}
                mediaUrls={mediaUrls}
                uploadingMedia={uploadingMedia}
                onMultipleUpload={handleMultipleMediaUpload}
                onRemoveImage={handleRemoveCourseImage}
                onRemoveVideo={handleRemoveCourseVideo}
                imageInputRef={imageInputRef}
                videoInputRef={videoInputRef}
              />}

              {currentStep === 2 && <Step2Schedule
                formData={formData}
                dateRangeRows={dateRangeRows}
                onDateRangeRowsChange={handleDateRangeRowsChange}
                onNested={handleNestedChange}
                onArrayRemove={handleArrayRemove}
                onTimeSlotAdd={handleTimeSlotAdd}
              />}

              {currentStep === 3 && <Step3Accommodation
                formData={formData}
                schoolRooms={schoolRooms}
                loadingRooms={loadingRooms}
                onArrayRemove={handleArrayRemove}
                onNested={handleNestedChange}
                onRoomAdd={handleRoomAdd}
                onOpenRoomTypes={() => {
                  if (!formData.schoolId) {
                    toast.error('Select a school first')
                    return
                  }
                  setRoomTypesModalOpen(true)
                }}
              />}

              {currentStep === 4 && (
                <Step4Review
                  formData={formData}
                  dateRangeRows={dateRangeRows}
                  selectedSchool={selectedSchool || undefined}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Step navigation */}
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-between">
          <button
            type="button"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft size={16} className="mr-2" />
            Previous
          </button>

          {currentStep === steps.length ? (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex items-center px-6 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={16} className="mr-2" />
              {isSubmitting ? 'Creating...' : 'Create Course'}
            </button>
          ) : (
            <button
              type="button"
              onClick={nextStep}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
            >
              Next
              <ArrowRight size={16} className="ml-2" />
            </button>
          )}
        </div>
      </div>
    </div>
    {formData.schoolId && (
      <RoomTypesManageModal
        isOpen={roomTypesModalOpen}
        onClose={() => setRoomTypesModalOpen(false)}
        schoolId={formData.schoolId}
        schoolName={selectedSchool?.name}
        onRoomsUpdated={() => {
          if (formData.schoolId) fetchSchoolRooms(formData.schoolId)
        }}
      />
    )}
    </>
  )
}

export default AddCourse

// ─────────────────────────────────────────────────────────────────────────
// Step components — split out purely to keep the parent readable. They share
// state via props so the wizard-level form data stays the single source of
// truth.

interface Step1Props {
  formData: CourseForm
  textareaValues: Record<string, string>
  setTextareaValues: React.Dispatch<React.SetStateAction<Record<string, string>>>
  onChange: (field: string, value: any) => void
  onTextarea: (field: string, value: string) => void
  getTextareaValue: (field: string, arr: string[] | undefined) => string
  onArrayAdd: (field: string, item: any) => void
  onArrayRemove: (field: string, index: number) => void
  mediaUrls: { images: string[]; videos: string[] }
  uploadingMedia: boolean
  onMultipleUpload: (files: File[], type: 'image' | 'video') => Promise<void>
  onRemoveImage: (index: number) => void
  onRemoveVideo: (index: number) => void
  imageInputRef: React.RefObject<HTMLInputElement>
  videoInputRef: React.RefObject<HTMLInputElement>
}

const Step1Basic: React.FC<Step1Props> = ({
  formData,
  setTextareaValues,
  onChange,
  onTextarea,
  getTextareaValue,
  onArrayAdd,
  onArrayRemove,
  mediaUrls,
  uploadingMedia,
  onMultipleUpload,
  onRemoveImage,
  onRemoveVideo,
  imageInputRef,
  videoInputRef,
}) => {
  const maxImages = MEDIA_SECTIONS.courseMedia.maxImages ?? 5
  const maxVideos = MEDIA_SECTIONS.courseMedia.maxVideos ?? 2
  const uploadedImageCount = (formData.media?.images || []).filter(
    (k) => k && !k.startsWith('blob:') && !k.startsWith('http://localhost'),
  ).length
  const uploadedVideoCount = (formData.media?.videos || []).filter(
    (k) => k && !k.startsWith('blob:') && !k.startsWith('http://localhost'),
  ).length

  return (
    <div className="space-y-8">
      {/* Name + Type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Course Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => onChange('name', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            placeholder="e.g., 200-Hour Yoga Teacher Training"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Course Type *</label>
          <select
            value={formData.type}
            onChange={(e) => onChange('type', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          >
            <option value="TRAINING">Yoga Teacher Training</option>
            <option value="RETREAT">Yoga Retreat</option>
          </select>
        </div>
      </div>

      {/* Duration + Difficulty */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Duration (Days) *</label>
          <input
            type="number"
            value={formData.duration}
            onChange={(e) => onChange('duration', parseInt(e.target.value))}
            onFocus={(e) => e.target.select()}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            min={1}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Difficulty Level</label>
          <select
            value={formData.difficulty || ''}
            onChange={(e) => onChange('difficulty', e.target.value || undefined)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          >
            <option value="">Select Difficulty (Optional)</option>
            <option value="ALL_LEVELS">All Levels</option>
            <option value="BEGINNER">Beginner</option>
            <option value="INTERMEDIATE">Intermediate</option>
            <option value="ADVANCED">Advanced</option>
          </select>
        </div>
      </div>

      {/* Currency + Max Students */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
          <select
            value={formData.currency}
            onChange={(e) => onChange('currency', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          >
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="INR">INR</option>
            <option value="IDR">IDR</option>
          </select>
          <p className="text-sm text-gray-500 mt-1">Currency for accommodation room pricing</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Max Students</label>
          <input
            type="number"
            value={formData.maxStudents || ''}
            onChange={(e) => onChange('maxStudents', e.target.value ? parseInt(e.target.value) : undefined)}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            min={1}
            placeholder="Optional"
          />
        </div>
      </div>

      {/* Yoga styles */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Yoga Styles</label>
        <p className="text-sm text-gray-600 mb-3">Select the styles taught in this course (multiple allowed)</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 p-4 border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
          {YOGA_STYLES.map((style) => (
            <label key={style} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
              <input
                type="checkbox"
                checked={formData.yogaStyles?.includes(style) || false}
                onChange={(e) => {
                  const current = formData.yogaStyles || []
                  if (e.target.checked) onChange('yogaStyles', [...current, style])
                  else onChange('yogaStyles', current.filter((s) => s !== style))
                }}
                className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
              />
              <span className="text-sm text-gray-700">{style}</span>
            </label>
          ))}
        </div>
        {formData.yogaStyles && formData.yogaStyles.length > 0 && (
          <div className="mt-3">
            <p className="text-xs text-gray-600 mb-2">Selected styles:</p>
            <div className="flex flex-wrap gap-2">
              {formData.yogaStyles.map((style, index) => (
                <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm">
                  {style}
                  <button
                    type="button"
                    onClick={() =>
                      onChange('yogaStyles', (formData.yogaStyles || []).filter((_, i) => i !== index))
                    }
                    className="ml-1 text-amber-600 hover:text-amber-800"
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Yoga Alliance Certified */}
      <div className="flex items-start justify-between p-4 border border-gray-200 rounded-xl bg-gray-50">
        <div className="space-y-1">
          <p className="text-sm font-semibold text-gray-700">Yoga Alliance Certified</p>
          <p className="text-sm text-gray-600">Mark this course as recognized by Yoga Alliance.</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={!!formData.yogaAllianceCertified}
          onClick={() => onChange('yogaAllianceCertified', !formData.yogaAllianceCertified)}
          className={`relative inline-flex shrink-0 h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${
            formData.yogaAllianceCertified ? 'bg-amber-600' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              formData.yogaAllianceCertified ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>

      {/* Description */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Short Description</label>
          <textarea
            value={formData.shortDescription || ''}
            onChange={(e) => onChange('shortDescription', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            rows={3}
            placeholder="Brief description (max 200 characters)"
            maxLength={200}
          />
          <div className="text-xs text-gray-500 mt-1">{(formData.shortDescription || '').length}/200 characters</div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Full Description *</label>
          <textarea
            value={formData.description}
            onChange={(e) => onChange('description', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            rows={4}
            placeholder="Detailed course description"
            maxLength={700}
            required
          />
          <div className="text-xs text-gray-500 mt-1">{(formData.description || '').length}/700 characters</div>
        </div>
      </div>

      {/* Inclusions + Requirements */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">What's Included</label>
          <div className="space-y-2">
            {formData.inclusions?.map((inclusion, index) => (
              <div key={index} className="flex items-center space-x-2">
                <input
                  type="text"
                  value={inclusion}
                  onChange={(e) => {
                    const newInclusions = [...(formData.inclusions || [])]
                    newInclusions[index] = e.target.value
                    onChange('inclusions', newInclusions)
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="e.g., Course materials, certificate"
                />
                <button
                  type="button"
                  onClick={() => onArrayRemove('inclusions', index)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => onArrayAdd('inclusions', '')}
              className="flex items-center text-amber-600 hover:text-amber-700 text-sm"
            >
              <span className="mr-1">+</span> Add Inclusion
            </button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Requirements</label>
          <textarea
            value={getTextareaValue('requirements', formData.requirements)}
            onChange={(e) => onTextarea('requirements', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            rows={4}
            placeholder={'Enter each requirement on a new line.\nBasic yoga experience\nMedical clearance'}
          />
          <p className="text-xs text-gray-500 mt-1">Enter each requirement on a new line.</p>
        </div>
      </div>

      {/* Who Is This For */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Who Is This For? *</label>
        <textarea
          value={getTextareaValue('whoIsThisFor', formData.whoIsThisFor)}
          onChange={(e) => onTextarea('whoIsThisFor', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          rows={4}
          placeholder={'Yoga practitioners seeking to deepen their practice\nAspiring yoga teachers\nThose looking for personal growth'}
        />
        <p className="text-xs text-gray-500 mt-1">Enter each target audience on a new line.</p>
      </div>

      {/* Highlights */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Course Highlights *</label>
        <textarea
          value={getTextareaValue('courseHighlights', formData.courseHighlights)}
          onChange={(e) => onTextarea('courseHighlights', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          rows={4}
          placeholder={'Yoga Alliance Accredited\nExperienced Teachers\nSmall Class Sizes'}
        />
        <p className="text-xs text-gray-500 mt-1">Enter each highlight on a new line.</p>
      </div>

      {/* Gain */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">What You Will Gain *</label>
        <textarea
          value={getTextareaValue('whatYouWillGain', formData.whatYouWillGain)}
          onChange={(e) => onTextarea('whatYouWillGain', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          rows={4}
          placeholder={'Tools to teach confidently\nDeeper personal practice\nRecognized certification'}
        />
        <p className="text-xs text-gray-500 mt-1">Enter each benefit on a new line.</p>
      </div>

      {/* Curriculum */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Detailed Curriculum</label>
        <div className="space-y-4">
          {formData.curriculum?.map((section, sectionIndex) => (
            <div key={sectionIndex} className="border border-gray-200 rounded-md p-4 space-y-3">
              <div className="flex items-center justify-between">
                <input
                  type="text"
                  value={section.title}
                  onChange={(e) => {
                    const newCurriculum = [...(formData.curriculum || [])]
                    newCurriculum[sectionIndex].title = e.target.value
                    onChange('curriculum', newCurriculum)
                  }}
                  placeholder="Section Title (e.g., Asana Practice, Philosophy)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 mr-2"
                />
                <button
                  type="button"
                  onClick={() => {
                    const newCurriculum = [...(formData.curriculum || [])]
                    newCurriculum.splice(sectionIndex, 1)
                    onChange('curriculum', newCurriculum)
                  }}
                  className="text-red-500 hover:text-red-700 p-2"
                >
                  <X size={16} />
                </button>
              </div>
              <textarea
                value={getTextareaValue(`curriculum_${sectionIndex}`, section.items)}
                onChange={(e) => {
                  const value = e.target.value
                  setTextareaValues((prev) => ({ ...prev, [`curriculum_${sectionIndex}`]: value }))
                  const newCurriculum = [...(formData.curriculum || [])]
                  newCurriculum[sectionIndex].items = value
                    .split('\n')
                    .map((item) => item.trim())
                    .filter((item) => item.length > 0)
                  onChange('curriculum', newCurriculum)
                }}
                placeholder="Enter each curriculum item on a new line"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                rows={3}
              />
            </div>
          ))}
          <button
            type="button"
            onClick={() => onChange('curriculum', [...(formData.curriculum || []), { title: '', items: [] }])}
            className="flex items-center text-amber-600 hover:text-amber-700 text-sm"
          >
            <span className="mr-1">+</span> Add Curriculum Section
          </button>
        </div>
      </div>

      {/* FAQ */}
      <div className="border-t border-gray-200 pt-6">
        <label className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <HelpCircle className="h-4 w-4 text-amber-600" />
          Course FAQ
        </label>
        <p className="text-xs text-gray-500 mb-3">Add frequently asked questions.</p>
        <div className="space-y-4">
          {(formData.faq || []).map((item, faqIdx) => (
            <div key={faqIdx} className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
              <div className="flex justify-between items-start gap-2">
                <input
                  type="text"
                  value={item.question}
                  onChange={(e) => {
                    const newFaq = [...(formData.faq || [])]
                    newFaq[faqIdx] = { ...item, question: e.target.value }
                    onChange('faq', newFaq)
                  }}
                  placeholder="Question"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                />
                <button
                  type="button"
                  onClick={() => onChange('faq', (formData.faq || []).filter((_, i) => i !== faqIdx))}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                  aria-label="Remove FAQ"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <textarea
                value={item.answer}
                onChange={(e) => {
                  const newFaq = [...(formData.faq || [])]
                  newFaq[faqIdx] = { ...item, answer: e.target.value }
                  onChange('faq', newFaq)
                }}
                placeholder="Answer"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
              />
            </div>
          ))}
          <button
            type="button"
            onClick={() => onChange('faq', [...(formData.faq || []), { question: '', answer: '' }])}
            className="flex items-center gap-2 text-sm font-medium text-amber-600 hover:text-amber-700"
          >
            <Plus className="h-4 w-4" /> Add FAQ
          </button>
        </div>
      </div>

      {/* Media */}
      <div className="border-t border-gray-200 pt-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">Course Media</label>

        {!formData.schoolId && (
          <p className="text-xs text-amber-700 mb-3">Select a school first to enable media uploads.</p>
        )}

        {/* Images */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Images (Max {maxImages})</label>
          <div className="flex flex-wrap gap-3 items-start">
            {mediaUrls.images.map((imageUrl, index) => (
              <div key={index} className="relative group">
                <img
                  src={imageUrl || 'https://via.placeholder.com/96'}
                  alt={`Course ${index + 1}`}
                  className="w-24 h-24 object-cover rounded-lg border-2 border-gray-200"
                  onError={(e) => {
                    ;(e.target as HTMLImageElement).src = 'https://via.placeholder.com/96'
                  }}
                />
                <button
                  type="button"
                  onClick={() => onRemoveImage(index)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100"
                  disabled={uploadingMedia}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            {uploadedImageCount < maxImages && (
              <div>
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploadingMedia || !formData.schoolId}
                  className="w-24 h-24 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-amber-500 hover:bg-amber-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadingMedia ? (
                    <Loader2 className="h-5 w-5 animate-spin text-amber-600" />
                  ) : (
                    <>
                      <Upload className="h-5 w-5 text-gray-400" />
                      <span className="text-xs text-gray-500">Add Image(s)</span>
                    </>
                  )}
                </button>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept={MEDIA_SECTIONS.courseMedia.acceptImages}
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const files = e.target.files ? Array.from(e.target.files) : []
                    if (files.length) onMultipleUpload(files, 'image')
                    e.target.value = ''
                  }}
                  disabled={uploadingMedia}
                />
              </div>
            )}
            {uploadedImageCount >= maxImages && (
              <p className="text-xs text-gray-500 w-full">Maximum {maxImages} images reached</p>
            )}
          </div>
        </div>

        {/* Videos */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Videos (Max {maxVideos})</label>
          <div className="flex flex-wrap gap-3 items-start">
            {mediaUrls.videos.map((videoUrl, index) => (
              <div key={index} className="relative group w-24 h-24 rounded-lg border-2 border-gray-200 bg-gray-900" title={videoUrl}>
                <div className="absolute inset-0 rounded-lg overflow-hidden">
                  {videoUrl && videoUrl.length > 0 ? (
                    <video
                      src={videoUrl}
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                      preload="metadata"
                      onError={(e) => {
                        ;(e.target as HTMLVideoElement).style.display = 'none'
                        const fallback = (e.target as HTMLVideoElement).nextElementSibling as HTMLElement
                        if (fallback) fallback.classList.remove('hidden')
                      }}
                    />
                  ) : null}
                  <div
                    className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br from-amber-200 to-amber-400 ${
                      videoUrl && videoUrl.length > 0 ? 'hidden' : ''
                    }`}
                  >
                    <Video className="h-8 w-8 text-amber-700" />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemoveVideo(index)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 z-10"
                  disabled={uploadingMedia}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            {uploadedVideoCount < maxVideos && (
              <div>
                <button
                  type="button"
                  onClick={() => videoInputRef.current?.click()}
                  disabled={uploadingMedia || !formData.schoolId}
                  className="w-24 h-24 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-amber-500 hover:bg-amber-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadingMedia ? (
                    <Loader2 className="h-5 w-5 animate-spin text-amber-600" />
                  ) : (
                    <>
                      <Upload className="h-5 w-5 text-gray-400" />
                      <span className="text-xs text-gray-500">Add Video</span>
                    </>
                  )}
                </button>
                <input
                  ref={videoInputRef}
                  type="file"
                  accept={MEDIA_SECTIONS.courseMedia.acceptVideos}
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) onMultipleUpload([file], 'video')
                    e.target.value = ''
                  }}
                  disabled={uploadingMedia}
                />
              </div>
            )}
            {uploadedVideoCount >= maxVideos && (
              <p className="text-xs text-gray-500 w-full">Maximum {maxVideos} videos reached</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

interface Step2Props {
  formData: CourseForm
  dateRangeRows: CourseDateRangeRow[]
  onDateRangeRowsChange: (rows: CourseDateRangeRow[]) => void
  onNested: (parent: string, field: string, value: any) => void
  onArrayRemove: (field: string, index: number) => void
  onTimeSlotAdd: () => void
}

const Step2Schedule: React.FC<Step2Props> = ({
  formData,
  dateRangeRows,
  onDateRangeRowsChange,
  onNested,
  onArrayRemove,
  onTimeSlotAdd,
}) => (
  <div className="space-y-6">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-3">Course date ranges *</label>
      <CourseDateRangesEditor rows={dateRangeRows} onChange={onDateRangeRowsChange} />
      {normalizeDateRangeRows(dateRangeRows).length > 0 && (
        <p className="text-sm text-amber-600 mt-3">
          ✓ {formatDateRangesSummary(normalizeDateRangeRows(dateRangeRows))}
        </p>
      )}
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Daily Schedule <span className="text-red-500">*</span>
      </label>
      <div className="space-y-3">
        {formData.schedule?.timeSlots?.map((slot, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-3 p-3 border border-gray-200 rounded-md">
            <input
              type="text"
              value={slot.label || ''}
              onChange={(e) => {
                const newSlots = [...(formData.schedule?.timeSlots || [])]
                newSlots[index].label = e.target.value
                onNested('schedule', 'timeSlots', newSlots)
              }}
              placeholder="Label (e.g., Morning Practice) *"
              required
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <input
              type="time"
              value={slot.startTime}
              onChange={(e) => {
                const newSlots = [...(formData.schedule?.timeSlots || [])]
                newSlots[index].startTime = e.target.value
                onNested('schedule', 'timeSlots', newSlots)
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <input
              type="time"
              value={slot.endTime}
              onChange={(e) => {
                const newSlots = [...(formData.schedule?.timeSlots || [])]
                newSlots[index].endTime = e.target.value
                onNested('schedule', 'timeSlots', newSlots)
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <button
              type="button"
              onClick={() => onArrayRemove('schedule.timeSlots', index)}
              className="text-red-500 hover:text-red-700 flex items-center justify-center"
            >
              <X size={16} />
            </button>
          </div>
        ))}
        <button type="button" onClick={onTimeSlotAdd} className="flex items-center text-amber-600 hover:text-amber-700">
          <span className="mr-1">+</span> Add Time Slot
        </button>
      </div>
    </div>
  </div>
)

interface Step3Props {
  formData: CourseForm
  schoolRooms: Room[]
  loadingRooms: boolean
  onArrayRemove: (field: string, index: number) => void
  onNested: (parent: string, field: string, value: any) => void
  onRoomAdd: () => void
  onOpenRoomTypes: () => void
}

const Step3Accommodation: React.FC<Step3Props> = ({
  formData,
  schoolRooms,
  loadingRooms,
  onArrayRemove,
  onNested,
  onRoomAdd,
  onOpenRoomTypes,
}) => (
  <div className="space-y-6">
    <div className="flex items-center space-x-2">
      <input
        type="checkbox"
        id="accommodationAvailable"
        checked={formData.accommodation?.available || false}
        onChange={(e) => onNested('accommodation', 'available', e.target.checked)}
        className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
      />
      <label htmlFor="accommodationAvailable" className="text-sm font-medium text-gray-700">
        Accommodation Available
      </label>
    </div>

    {formData.accommodation?.available && (
      <>
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <label className="block text-sm font-medium text-gray-700">Room Types</label>
            <button
              type="button"
              onClick={onOpenRoomTypes}
              className="text-sm font-medium text-amber-600 hover:text-amber-700"
            >
              Manage room types
            </button>
          </div>
          {loadingRooms ? (
            <p className="text-sm text-gray-500">Loading rooms...</p>
          ) : !schoolRooms || schoolRooms.length === 0 ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800 font-medium mb-2">
                No room types yet. Create accommodation room types first, then add them here.
              </p>
              <p className="text-sm text-amber-700 mb-3">
                Open the window below to add room types. Your course draft stays on this page and the list updates
                when you are done.
              </p>
              <button
                type="button"
                onClick={onOpenRoomTypes}
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Home size={18} />
                Add room types
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-3 rounded-md bg-blue-50 border border-blue-200">
                <p className="text-sm text-blue-800">
                  The <span className="font-semibold">Price</span> below is the{' '}
                  <span className="font-semibold">course price for that accommodation type</span>.
                </p>
              </div>
              {formData.accommodation?.rooms?.map((room, index) => {
                const selectedRoom = schoolRooms.find((r) => r._id === room.roomId)
                const selectedElsewhere = (formData.accommodation?.rooms || [])
                  .map((r, i) => (i !== index && r.roomId ? r.roomId : null))
                  .filter(Boolean) as string[]
                const availableRooms = schoolRooms.filter(
                  (r) => r._id === room.roomId || !selectedElsewhere.includes(r._id),
                )
                return (
                  <div key={index} className="p-4 border border-gray-200 rounded-md space-y-3">
                    <div className="flex items-start gap-3">
                      <select
                        value={room.roomId || ''}
                        onChange={(e) => {
                          const selectedRoomId = e.target.value
                          const selectedRoomData = schoolRooms.find((r) => r._id === selectedRoomId)
                          if (selectedRoomData) {
                            const newRooms = [...(formData.accommodation?.rooms || [])]
                            newRooms[index] = {
                              roomId: selectedRoomData._id,
                              type: selectedRoomData.type,
                              capacity: selectedRoomData.capacity,
                              price: newRooms[index]?.price ?? 0,
                              bookingAmount: newRooms[index]?.bookingAmount,
                              amenities: [],
                            }
                            onNested('accommodation', 'rooms', newRooms)
                          }
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-amber-500 focus:border-amber-500"
                        required
                      >
                        <option value="">Select a room type</option>
                        {availableRooms.map((r) => (
                          <option key={r._id} value={r._id}>
                            {r.name} ({r.type}) - Capacity: {r.capacity}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => onArrayRemove('accommodation.rooms', index)}
                        className="text-red-500 hover:text-red-700 p-2 flex-shrink-0"
                        aria-label="Remove room"
                      >
                        <X size={16} />
                      </button>
                    </div>
                    {selectedRoom && (
                      <p className="text-sm text-gray-500">
                        Capacity: {selectedRoom.capacity} | Type: {selectedRoom.type}
                      </p>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Course price for this accommodation ({formData.currency})
                        </label>
                        <input
                          type="number"
                          value={room.price || ''}
                          onChange={(e) => {
                            const newRooms = [...(formData.accommodation?.rooms || [])]
                            newRooms[index].price = parseFloat(e.target.value) || 0
                            onNested('accommodation', 'rooms', newRooms)
                          }}
                          onWheel={(e) => (e.target as HTMLInputElement).blur()}
                          placeholder="Room price"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-amber-500"
                          min={0}
                          step={0.01}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Booking amount ({formData.currency}) *
                        </label>
                        <input
                          type="number"
                          value={room.bookingAmount ?? ''}
                          onChange={(e) => {
                            const val = e.target.value === '' ? undefined : parseFloat(e.target.value)
                            const newRooms = [...(formData.accommodation?.rooms || [])]
                            newRooms[index].bookingAmount = val ?? undefined
                            onNested('accommodation', 'rooms', newRooms)
                          }}
                          onWheel={(e) => (e.target as HTMLInputElement).blur()}
                          placeholder="Booking amount (required)"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-amber-500"
                          min={0}
                          step={0.01}
                          required
                        />
                        <p className="text-xs text-gray-500 mt-0.5">
                          Must be more than 10% of price and not more than the price.
                        </p>
                      </div>
                    </div>
                  </div>
                )
              })}
              {schoolRooms.length > (formData.accommodation?.rooms?.length ?? 0) && (
                <button
                  type="button"
                  onClick={onRoomAdd}
                  className="flex items-center text-amber-600 hover:text-amber-700 text-sm"
                >
                  <span className="mr-1">+</span> Add Room Type
                </button>
              )}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Meal Plan</label>
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="mealPlanIncluded"
                checked={formData.accommodation?.mealPlan?.included || false}
                onChange={(e) =>
                  onNested('accommodation', 'mealPlan', {
                    ...(formData.accommodation?.mealPlan || { included: false, type: 'VEGETARIAN' }),
                    included: e.target.checked,
                  })
                }
                className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
              />
              <label htmlFor="mealPlanIncluded" className="text-sm font-medium text-gray-700">
                Meals Included
              </label>
            </div>
            {formData.accommodation?.mealPlan?.included && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Meal Type</label>
                  <select
                    value={formData.accommodation?.mealPlan?.type || 'VEGETARIAN'}
                    onChange={(e) =>
                      onNested('accommodation', 'mealPlan', {
                        ...(formData.accommodation?.mealPlan || { included: true, type: 'VEGETARIAN' }),
                        type: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-amber-500"
                  >
                    <option value="VEGETARIAN">Vegetarian</option>
                    <option value="VEGAN">Vegan</option>
                    <option value="OMNIVORE">Omnivore</option>
                    <option value="CUSTOM">Custom</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Meal Plan Description</label>
                  <textarea
                    value={formData.accommodation?.mealPlan?.description || ''}
                    onChange={(e) =>
                      onNested('accommodation', 'mealPlan', {
                        ...(formData.accommodation?.mealPlan || { included: true, type: 'VEGETARIAN' }),
                        description: e.target.value,
                      })
                    }
                    placeholder="e.g. Three vegetarian meals per day"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-amber-500 min-h-[80px] resize-y"
                    rows={3}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    )}
  </div>
)

interface Step4Props {
  formData: CourseForm
  dateRangeRows: CourseDateRangeRow[]
  selectedSchool: YogaSchool | undefined
}

const Step4Review: React.FC<Step4Props> = ({ formData, dateRangeRows, selectedSchool }) => (
  <div className="space-y-6">
    <div className="bg-gray-50 rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Course Summary</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Basic Information</h4>
          <div className="space-y-1 text-sm text-gray-600">
            <p>
              <span className="font-medium">School:</span>{' '}
              {selectedSchool ? `${selectedSchool.name}${selectedSchool.city ? ` — ${selectedSchool.city}` : ''}` : '—'}
            </p>
            <p>
              <span className="font-medium">Name:</span> {formData.name}
            </p>
            <p>
              <span className="font-medium">Type:</span>{' '}
              {formData.type === 'TRAINING' ? 'Yoga Teacher Training' : 'Yoga Retreat'}
            </p>
            <p>
              <span className="font-medium">Duration:</span> {formData.duration} days
            </p>
            {formData.difficulty && (
              <p>
                <span className="font-medium">Difficulty:</span> {formData.difficulty}
              </p>
            )}
            {formData.maxStudents && (
              <p>
                <span className="font-medium">Max Students:</span> {formData.maxStudents}
              </p>
            )}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Schedule</h4>
          <div className="space-y-1 text-sm text-gray-600">
            <p>
              <span className="font-medium">Start dates:</span>{' '}
              {normalizeDateRangeRows(dateRangeRows).length > 0
                ? formatDateRangesSummary(normalizeDateRangeRows(dateRangeRows))
                : 'None added'}
            </p>
            <p>
              <span className="font-medium">Time Slots:</span> {formData.schedule?.timeSlots?.length || 0}
            </p>
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Accommodation</h4>
          <div className="space-y-1 text-sm text-gray-600">
            <p>
              <span className="font-medium">Available:</span> {formData.accommodation?.available ? 'Yes' : 'No'}
            </p>
            {formData.accommodation?.available && (
              <>
                <p>
                  <span className="font-medium">Room Types:</span> {formData.accommodation?.rooms?.length || 0}
                </p>
                <p>
                  <span className="font-medium">Meals Included:</span>{' '}
                  {formData.accommodation?.mealPlan?.included ? 'Yes' : 'No'}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
      {formData.description && (
        <div className="mt-4">
          <h4 className="font-medium text-gray-900 mb-2">Description</h4>
          <p className="text-sm text-gray-600">{formData.description}</p>
        </div>
      )}
    </div>
  </div>
)
